import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, CalendarCheck, DollarSign, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Conversion nudge modal — fires 15 seconds after a demo user's FIRST
 * successful quick-delivery.  Prompts them to book a real demo via Calendly
 * or head to the pricing page.  Tracks every interaction (`nudge_shown`,
 * `nudge_clicked_book`, `nudge_clicked_pricing`, `nudge_dismissed`) via
 * POST /api/demo/track so the admin funnel widget can visualise conversion.
 *
 * Guarded by a localStorage flag keyed on the demo user id so the nudge
 * only fires once per demo session, even across page reloads.
 */

const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || "https://calendly.com/dt6/demo";
const NUDGE_DELAY_MS = 15000;

export default function DemoConversionNudge({ triggerKey }) {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user?.demo || !triggerKey) return;
    const flagKey = `dt6_demo_nudge_shown_${user.id}`;
    if (localStorage.getItem(flagKey)) return;

    const timer = setTimeout(() => {
      setVisible(true);
      localStorage.setItem(flagKey, String(Date.now()));
      api.post("/demo/track", { event: "nudge_shown", meta: { trigger: triggerKey } })
        .catch(() => {});
    }, NUDGE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [user?.demo, user?.id, triggerKey]);

  const track = (event, extra = {}) => {
    api.post("/demo/track", { event, meta: extra }).catch(() => {});
  };

  const bookDemo = () => {
    track("nudge_clicked_book", { url: CALENDLY_URL });
    window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
    setTimeout(() => setVisible(false), 400);
  };

  const goPricing = () => {
    track("nudge_clicked_pricing");
    setVisible(false);
  };

  const dismiss = () => {
    track("nudge_dismissed");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9997] flex items-center justify-center px-4"
      style={{ background: "rgba(6,10,20,0.72)", backdropFilter: "blur(6px)" }}
      onClick={dismiss}
      data-testid="demo-nudge-backdrop"
    >
      <div
        className="dt6-card w-full max-w-lg p-8 relative dt6-fade-in"
        style={{ background: "var(--dt6-surface)", borderColor: "var(--dt6-primary)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="demo-nudge"
      >
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 text-[color:var(--dt6-muted)] hover:text-[color:var(--dt6-fg)]"
          data-testid="demo-nudge-close"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="dt6-chip dt6-chip-primary mb-4">
          <Sparkles size={12} className="inline-block mr-1" /> / LOVED THAT?
        </div>
        <h2 className="font-heading text-3xl font-bold mb-3">
          That was a synthetic demo. Let's do it for real.
        </h2>
        <p className="text-sm text-[color:var(--dt6-muted)] mb-6">
          You just watched SIX auto-deliver a SIMULATOR file end-to-end. Book a 20-minute
          walk-through with a DT6 operator and we'll wire the same pipeline against your
          actual voter file — TCPA-clean, delivered in 72 hours.
        </p>

        <div className="grid gap-3">
          <button
            onClick={bookDemo}
            className="dt6-btn dt6-btn-primary w-full justify-center"
            data-testid="demo-nudge-book-btn"
          >
            <CalendarCheck size={16} /> Book a live 20-min demo
          </button>
          <Link
            to="/pricing"
            onClick={goPricing}
            className="dt6-btn w-full justify-center"
            data-testid="demo-nudge-pricing-btn"
          >
            <DollarSign size={14} /> See real pricing first
          </Link>
        </div>

        <p className="text-[0.7rem] tracking-wide text-[color:var(--dt6-muted)] mt-4 font-mono uppercase">
          / You'll only see this once per demo session.
        </p>
      </div>
    </div>
  );
}
